import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import usePermissions from '../../hooks/usePermissions';
import { ROUTE_PERMISSIONS, getFirstAllowedRoute } from '../../config/permissions';

export default function ProtectedRoute({ allowedRoles }) {
  const { user, loading } = useAuth();
  const { hasPermission, permissions } = usePermissions();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B5D1E]"></div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  // Check route-level permissions for admin pages
  const requiredPerms = ROUTE_PERMISSIONS[location.pathname];
  if (requiredPerms && user.role === 'admin' && !hasPermission(...requiredPerms)) {
    // Send them somewhere they CAN go, instead of a dead-end link back to a denied dashboard
    const fallbackRoute = getFirstAllowedRoute(permissions);
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-500 mb-6">
            {fallbackRoute
              ? "You don't have permission to access this page."
              : 'No admin permissions have been assigned to your account yet. Ask a super admin to add permissions to your role.'}
          </p>
          {fallbackRoute && (
            <a href={fallbackRoute} className="text-[#0B5D1E] hover:underline font-semibold">
              Go to an accessible page
            </a>
          )}
        </div>
      </div>
    );
  }

  return <Outlet />;
}
